/**
 * Le découpé d'un exercice d'ouvrage, en vignette.
 *
 * Hedi, le 25 septembre 2026 : *« Quand tu affiche : Exercice 1 — page 7, je
 * veux aussi voir le découpé de l'exercice pas seulement le transcript »*. Un
 * exercice d'ouvrage **n'a pas de texte** de notre côté — il est découpé en
 * images —, donc un numéro, une page et un libellé de compétence ne disent pas
 * ce que l'exercice demande.
 *
 * **Deux écrans s'en servent** — le sommaire d'un chapitre, où le parent
 * compose, et la liste des entraînements, où il relit ce qu'il a composé — d'où
 * ce composant partagé plutôt qu'un bloc recopié : deux copies finissent par
 * plafonner à deux hauteurs différentes, et le parent lit alors deux écrans qui
 * ne montrent pas la même chose.
 *
 * Ce n'est **pas** `EnonceImage`, qui est le lecteur zoomable de l'enfant
 * devant *un* exercice : ici il y en a trente sur la page, et trente lecteurs à
 * gestes montés d'un coup coûtent bien plus que ce qu'ils rendraient — le
 * parent parcourt pour choisir, il ne résout pas.
 *
 * L'URL est signée et de courte durée de vie (règle 8), et elle est signée par
 * la route du parent. Le corrigé ne passe pas par ici.
 *
 * **Toutes les pages, l'une sous l'autre** : un exercice qui passe d'une colonne
 * à l'autre du cahier est découpé en plusieurs rectangles, et n'en montrer que le
 * premier ferait choisir le parent sur la moitié d'un énoncé.
 */

import { useState } from "react";

/**
 * Les pages à montrer : la liste quand le contrat la porte, sinon l'URL seule
 * (serveur plus ancien, ou exercice d'une seule page).
 */
export function pagesDe(
  urls: readonly (string | null | undefined)[] | null | undefined,
  url?: string | null,
): string[] {
  const pages = (urls ?? []).map((page) => (page ?? "").trim()).filter(Boolean);
  if (pages.length) return pages;
  const seule = (url ?? "").trim();
  return seule ? [seule] : [];
}

export function Decoupe({ urls, intitule }: { urls: readonly string[]; intitule: string }) {
  const [agrandi, setAgrandi] = useState(false);

  // Pas de cadre vide quand la réserve n'a pas l'image : un cadre vide se lit
  // comme une panne.
  if (!urls.length) return null;

  return (
    <button
      type="button"
      className={`decoupe${agrandi ? " decoupe--grand" : ""}`}
      onClick={() => setAgrandi((avant) => !avant)}
      aria-expanded={agrandi}
    >
      {/* `loading="lazy"` : un chapitre en tient trente, et trente images
          signées demandées d'un coup à l'ouverture, ce sont trente attentes
          pour deux vignettes regardées. */}
      {urls.map((url, rang) => (
        <img
          key={`${rang}-${url}`}
          src={url}
          alt={
            urls.length > 1
              ? `Le découpé de l'${intitule.toLowerCase()}, partie ${rang + 1} sur ${urls.length}`
              : `Le découpé de l'${intitule.toLowerCase()}`
          }
          loading="lazy"
        />
      ))}
      <span className="muted small">{agrandi ? "Réduire" : "Agrandir"}</span>
    </button>
  );
}
